import type { FC } from 'react';
import React, { useMemo } from 'react';
import {
  ReceiptBatchMangopayPaymentCard,
  ReceiptBatchPaymentCard,
  ReceiptBatchSummaryCardDetailed,
} from '@pflegenavi/shared-web/receipt-batch';
import {
  MangopayPaymentStatus,
  useGetReceiptBatchPaymentPhoenix,
  useGetReceiptImagesWithTenant,
  useGetServiceProviderPhoenix,
  useServiceProvider,
  useServiceProviderReceiptBatchPayment,
} from '@pflegenavi/service-provider-frontend/api';
import type { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ReceiptBatchPaymentState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderReceiptBatchDetails } from './form/ServiceProviderReceiptBatchDetails';
import type { BatchReceiptDetails } from './form/ServiceProviderReceiptBatchForm';
import { ServiceProviderStripeOperatedBy } from '@pflegenavi/shared/api';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';

interface ReceiptBatchDetailsContainerProps {
  id: string | undefined;
  isLoading: boolean;
  receipts: BatchReceiptDetails[];
  autoGenerateReceipts?: boolean;
  generateReceiptBatchPreview?: () => Promise<Blob | undefined>;
  autoGenerateReceiptsPreview?: (entryId: string) => Promise<Blob | undefined>;
  totalAmount: number;
  receiptsCount: number;
  batchImageIds?: string[];
  receiptDate?: Date;
  nursingHomeName?: string;
  tenant?: string;
  state?: ReceiptBatchState;
  lastUpdateDate?: Date;
  useIndividualDates: boolean;
  minReceiptDate?: Date;
  maxReceiptDate?: Date;
}

export const ReceiptBatchDetailsContainer: FC<
  ReceiptBatchDetailsContainerProps
> = ({
  id,
  isLoading,
  receipts,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
  tenant,
  batchImageIds,
  nursingHomeName,
  receiptsCount,
  totalAmount,
  receiptDate,
  state,
  lastUpdateDate,
  maxReceiptDate,
  minReceiptDate,
  useIndividualDates,
  generateReceiptBatchPreview,
}) => {
  const useGetReceiptImages = useGetReceiptImagesWithTenant({
    tenant: tenant ?? '',
  });

  const globalServiceProviderId =
    useGetGlobalServiceProviderId().globalServiceProviderId;

  const { data: company } = useServiceProvider({
    globalServiceProviderId: globalServiceProviderId ?? '',
  });

  const { data: paymentStatus } = useServiceProviderReceiptBatchPayment({
    receiptBatchId: id,
  });

  const { data: serviceProviderProfile } = useGetServiceProviderPhoenix();

  const isMangopay = useMemo(
    () => !!serviceProviderProfile?.data.mangopay_user_id,
    [serviceProviderProfile?.data.mangopay_user_id]
  );

  const { data: paymentPhoenix } = useGetReceiptBatchPaymentPhoenix({
    receiptBatchId: id,
    options: {
      enabled: isMangopay,
    },
  });

  const mangopayPaidAmount = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries
      .filter((entry) => entry.status === MangopayPaymentStatus.Paid)
      .reduce((acc, entry) => acc + entry.amount, 0);
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayPaidAdditionalFeeAmount = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries
      .filter((entry) => entry.status === MangopayPaymentStatus.Paid)
      .reduce((acc, entry) => acc + (entry.additional_fee_amount ?? 0), 0);
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayPendingReceipts = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) => entry.status === MangopayPaymentStatus.Pending
    );
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const mangopayCancelledReceipts = useMemo(() => {
    return paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) => entry.cancellation != null
    );
  }, [paymentPhoenix?.data.receipt_batch_payment.receipt_batch_entries]);

  const receiptBatchPaymentCard = useMemo(() => {
    if (isMangopay && paymentPhoenix?.data) {
      return (
        <ReceiptBatchMangopayPaymentCard
          isLoading={false}
          nursingHomeName={nursingHomeName}
          paidAmountInCents={mangopayPaidAmount ?? 0}
          paidAdditionalFeeAmountInCents={mangopayPaidAdditionalFeeAmount ?? 0}
          pendingAmountInCents={(mangopayPendingReceipts ?? []).reduce(
            (acc, entry) => acc + entry.amount,
            0
          )}
          pendingAdditionalFeeAmountInCents={(
            mangopayPendingReceipts ?? []
          ).reduce((acc, entry) => acc + (entry.additional_fee_amount ?? 0), 0)}
          pendingCount={mangopayPendingReceipts?.length ?? 0}
          cancelledAmountInCents={(mangopayCancelledReceipts ?? []).reduce(
            (acc, entry) => acc + entry.amount,
            0
          )}
          cancelledCount={mangopayCancelledReceipts?.length ?? 0}
        />
      );
    }
    if (
      company?.stripeOperatedBy !==
        ServiceProviderStripeOperatedBy.Pflegenavi &&
      paymentStatus?.status === ReceiptBatchPaymentState.PAYMENT_INITIATED
    ) {
      return (
        <ReceiptBatchPaymentCard
          isLoading={false}
          nursingHomeName={paymentStatus.payment.nursingHome.name}
          payment={{
            id: paymentStatus.payment.id,
            amountInCents: paymentStatus.payment.amount,
            completedAt: paymentStatus.payment.completedAt ?? undefined,
            initiationDate: paymentStatus.payment.created,
            status: paymentStatus.payment.status,
          }}
        />
      );
    }
    return undefined;
  }, [
    mangopayPaidAdditionalFeeAmount,
    company?.stripeOperatedBy,
    isMangopay,
    mangopayPaidAmount,
    mangopayPendingReceipts,
    mangopayCancelledReceipts,
    nursingHomeName,
    paymentStatus,
    paymentPhoenix?.data,
  ]);

  if (!tenant || !nursingHomeName || !company) {
    return null;
  }

  return (
    <ServiceProviderReceiptBatchDetails
      isLoading={isLoading}
      receipts={receipts}
      payment={paymentPhoenix}
      autoGenerateReceipts={autoGenerateReceipts}
      autoGenerateReceiptsPreview={autoGenerateReceiptsPreview}
      useReceiptImages={useGetReceiptImages}
      width={330}
      receiptDate={receiptDate}
      useIndividualDates={useIndividualDates}
      ReceiptBatchSummaryCard={
        <ReceiptBatchSummaryCardDetailed
          useReceiptImages={useGetReceiptImages}
          title={nursingHomeName ?? ''}
          receiptDate={new Date(receiptDate ?? Date.now())}
          useIndividualDates={useIndividualDates}
          minReceiptDate={minReceiptDate}
          maxReceiptDate={maxReceiptDate}
          totalAmount={totalAmount}
          totalAdditionalFeeAmount={0}
          totalIncludedFeeAmount={0}
          receiptsCount={receiptsCount}
          receiptImageIds={batchImageIds}
          lastUpdateDate={lastUpdateDate}
          state={state ?? 'draft'}
          autoGenerateReceipts={autoGenerateReceipts}
          generateReceiptBatchPreview={generateReceiptBatchPreview}
        />
      }
      ReceiptBatchPaymentCard={receiptBatchPaymentCard}
    />
  );
};
